// Minnesotaänger Mini App — minimal UI environment bootstrap.
// Restores the DOM environment telegram-tt set up before first render,
// trimmed to exactly what the Mini App SCSS depends on. No account /
// session / cache machinery (that was the bulk of telegram-tt's
// global/actions/ui/initial.ts shouldInit callback, cut in v1).
//
// Sources (pre-redesign Web A, 0df85b8d semantics):
//   - global/actions/ui/initial.ts   text-size CSS variables + body
//     platform/touch classes + switchTheme call
//   - util/switchTheme.ts            html.theme-* class + --color-* injection
//   - components/App.tsx             body --theme-background-color

import { requestMutation } from '../lib/fasterdom/fasterdom';
import {
  IS_ANDROID,
  IS_IOS,
  IS_LINUX,
  IS_MAC_OS,
  IS_SAFARI,
  IS_TOUCH_ENV,
  IS_WINDOWS,
} from './browser/windowEnvironment';
import { DARK_THEME_BG_COLOR, LIGHT_THEME_BG_COLOR } from '../config';
import themeColors from '../styles/themes.json';

// v1: message text size is fixed at the telegram-tt default (the settings
// screen that let users change it was trimmed).
const MESSAGE_TEXT_SIZE_PX = 16;

export function initAppEnvironment() {
  requestMutation(() => {
    const isDark = getIsDarkTheme();

    // Theme class + color variables (ui/initial `switchTheme` semantic).
    document.documentElement.classList.remove('theme-light', 'theme-dark');
    document.documentElement.classList.add(isDark ? 'theme-dark' : 'theme-light');

    (Object.keys(themeColors) as Array<keyof typeof themeColors>).forEach((property) => {
      const [light, dark] = themeColors[property];
      const color = isDark ? dark : light;
      document.documentElement.style.setProperty(property, color);

      // telegram-tt's switchTheme also injects `-rgb` companions for the
      // variables SCSS consumes via rgba(var(--color-*-rgb), …). Mini App
      // SCSS uses them too (Button.scss, Audio.scss), so inject one for
      // every color key.
      const rgb = hexToRgb(color);
      if (rgb) {
        document.documentElement.style.setProperty(`${property}-rgb`, rgb);
      }
    });

    // Body environment classes (ui/initial semantic).
    document.body.classList.add(IS_TOUCH_ENV ? 'is-touch-env' : 'is-pointer-env');
    if (IS_IOS) {
      document.body.classList.add('is-ios');
    } else if (IS_ANDROID) {
      document.body.classList.add('is-android');
    } else if (IS_MAC_OS) {
      document.body.classList.add('is-macos');
    } else if (IS_WINDOWS) {
      document.body.classList.add('is-windows');
    } else if (IS_LINUX) {
      document.body.classList.add('is-linux');
    }
    if (IS_SAFARI) {
      document.body.classList.add('is-safari');
    }

    // Message text-size variables (ui/initial semantic).
    document.documentElement.style.setProperty(
      '--composer-text-size',
      `${Math.max(MESSAGE_TEXT_SIZE_PX, IS_IOS ? 16 : 15)}px`,
    );
    document.documentElement.style.setProperty(
      '--message-meta-height',
      `${Math.floor(MESSAGE_TEXT_SIZE_PX * 1.25)}px`,
    );
    document.documentElement.style.setProperty('--message-text-size', `${MESSAGE_TEXT_SIZE_PX}px`);
    document.documentElement.setAttribute('data-message-text-size', String(MESSAGE_TEXT_SIZE_PX));

    // Wallpaper background color (App.tsx semantic: body --theme-background-color).
    document.body.style.setProperty(
      '--theme-background-color',
      isDark ? DARK_THEME_BG_COLOR : LIGHT_THEME_BG_COLOR,
    );
  });
}

function getIsDarkTheme(): boolean {
  // The Mini App follows the Telegram client color scheme.
  const colorScheme = (window as typeof window & {
    Telegram?: { WebApp?: { colorScheme?: 'light' | 'dark' } };
  }).Telegram?.WebApp?.colorScheme;
  return colorScheme === 'dark';
}

function hexToRgb(hex: string): string | undefined {
  // themes.json entries are #RRGGBB or #RRGGBBAA.
  const m = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(hex);
  if (!m) return undefined;
  const value = parseInt(m[1], 16);
  return `${(value >> 16) & 0xff}, ${(value >> 8) & 0xff}, ${value & 0xff}`;
}
